import { useCallback, useEffect, useMemo, useState } from "react";
import { getMe, loadFacts, setMe as setApiMe } from "./api";
import { AskHR, type Prefill } from "./components/AskHR";
import { Identity } from "./components/Identity";
import { RecordTab } from "./components/RecordTab";
import { SignIn } from "./components/SignIn";
import { Tabs, tabFromHash } from "./components/Tabs";
import { Telegram } from "./components/Telegram";
import { CalendarIcon, CardIcon, PulseIcon } from "./components/icons";
import { CARD_QUESTIONS, MOCK, MOCK_ME, MOCK_S_PASS_ANSWER } from "./mock";
import type { ChatMessage, FactKey, Facts, Me, TabKey } from "./types";

/** Record tab → the facts key it reports. */
const RECORD_KEYS: Record<"leave" | "payslips" | "sick", FactKey> = {
  leave: "leave",
  payslips: "payslip",
  sick: "mc",
};

const MISSING = "Your HR system hasn't reported this field yet.";

type Phase = "loading" | "in" | "out";

export function App() {
  const [phase, setPhase] = useState<Phase>("loading");
  const [me, setMe] = useState<Me | null>(null);
  const [tab, setTab] = useState<TabKey>(tabFromHash);
  const [facts, setFacts] = useState<Facts | null>(null);
  const [factsError, setFactsError] = useState<string | null>(null);
  const [prefill, setPrefill] = useState<Prefill | null>(null);

  // Tabs are keyed to the hash, so they deep-link and the back button works.
  useEffect(() => {
    const onHash = () => setTab(tabFromHash());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const readRecords = useCallback(async (force?: boolean) => {
    setFacts(null);
    setFactsError(null);
    try {
      setFacts(await loadFacts(force));
    } catch (e) {
      setFactsError(String((e as Error).message || e));
    }
  }, []);

  useEffect(() => {
    void (async () => {
      const who = MOCK ? MOCK_ME : await getMe();
      if (!who) {
        setPhase("out");
        return;
      }
      setApiMe(who);
      setMe(who);
      setPhase("in");
      void readRecords();
    })();
  }, [readRecords]);

  // The log opens empty. Offline it opens on the scripted exchange that shows
  // the assistant answering for real.
  const boot = useMemo<ChatMessage[]>(() => {
    if (!me || !MOCK) return [];
    return [
      { id: 0, role: "tool", text: "— mock mode: canned data, no network —" },
      {
        id: 1,
        role: "user",
        text: "What is the minimum salary for a new S Pass application?",
      },
      { id: 2, role: "assistant", text: MOCK_S_PASS_ANSWER },
    ];
  }, [me]);

  const askAbout = (key: FactKey) => {
    setPrefill((p) => ({ text: CARD_QUESTIONS[key], nonce: (p?.nonce ?? 0) + 1 }));
    location.hash = "#ask";
    setTab("ask");
  };

  const recordProps = (which: keyof typeof RECORD_KEYS) => {
    const key = RECORD_KEYS[which];
    const sentence = facts ? (facts[key] ?? null) : null;
    return {
      sentence,
      error: factsError ?? (facts && !sentence ? MISSING : null),
      onAsk: () => askAbout(key),
      onRefresh: () => void readRecords(true),
    };
  };

  if (phase === "loading") return null;

  return (
    <>
      <div className="topbar">
        <b>WorkJoy</b>
        <span className="tag">HR AI · Employee Portal</span>
        {me && <Identity me={me} />}
      </div>

      <main>
        {phase === "out" && <SignIn />}

        {me && (
          <>
            <Tabs active={tab} />

            <section hidden={tab !== "ask"}>
              <AskHR boot={boot} prefill={prefill} />
            </section>

            <section hidden={tab !== "leave"}>
              <RecordTab icon={<CalendarIcon />} title="Annual Leave" {...recordProps("leave")} />
            </section>

            <section hidden={tab !== "payslips"}>
              <RecordTab icon={<CardIcon />} title="Next Payslip" {...recordProps("payslips")} />
            </section>

            <section hidden={tab !== "sick"}>
              <RecordTab icon={<PulseIcon />} title="MC / Sick Days" {...recordProps("sick")} />
            </section>

            <section hidden={tab !== "telegram"}>
              <Telegram />
            </section>
          </>
        )}
      </main>
    </>
  );
}
